import { Box, Dialog, DialogContent, Grid, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import { useActivityDialog } from "../../hooks/useActivityDialog";
import { useGetCalendarEventByIdQuery } from "../../store/ms-services/calendarApi";
import { WorkItemStateEnum } from "../../store/services/timelineApiRTK";
import { ActivityDialogProps } from "../../types/ActivityDialog.types";
import CompleteActionDialog from "../activity-complete-dialog/CompleteActionDialog";
import MoveWorkItemDialog from "../activity-move-dialog/MoveWorkItemDialog";
import AssignWorkItemDialog from "../dialogs/AssignWorkItemDialog";
import MeetingCard from "../meeting-card/MeetingCard";
import CalendarEventError from "../calendar-event-error/CalendarEventError";
import { useWorkPanel } from "../../hooks/useWorkPanel";
import ActionPanel from "./ActionPanel";
import ActivityDialogFooter from "./ActivityDialogFooter";
import ActivityDialogHeader from "./ActivityDialogHeader";
import CustomerInfoCard from "./CustomerInfoCard";
import WorkDetailsCard from "./WorkDetailsCard";

const ActivityDialog: React.FC<ActivityDialogProps> = ({
  open,
  onClose,
  activity,
}) => {
  const {
    showCompleteDialog,
    setShowCompleteDialog,
    showMoveDialog,
    setShowMoveDialog,
    handleCustomerClick,
    handleFinishClicked,
    handleClose,
    handleToggleFavorite,
    handleUpdateDescription,
    setShowAssignDialog,
    showAssignDialog,
  } = useActivityDialog(activity, onClose);

  const { openWork } = useWorkPanel();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const hasExternalId = Boolean(activity?.ExternalId);
  const hideMainDialog =
    !open ||
    showCompleteDialog ||
    showMoveDialog ||
    showAssignDialog;

  const handleOpenWork = () => {
    if (activity?.WorkId) {
      openWork(activity.WorkId);
    }
  };

  const {
    data: calendarEvent,
    isLoading: isCalendarLoading,
    error: calendarError,
  } = useGetCalendarEventByIdQuery(activity?.ExternalId || "", {
    skip: !hasExternalId,
  });

  // Check if calendar event was not found (404 error)
  const isCalendarEventNotFound = 
    calendarError && 
    'status' in calendarError && 
    calendarError.status === 404;

  // Check if we should show calendar-related content
  const shouldShowCalendarContent = hasExternalId && (calendarEvent || isCalendarLoading || isCalendarEventNotFound);

  if (!activity) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open && !hideMainDialog}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            overflow: "hidden",
          },
        }}
      >
        <ActivityDialogHeader
          activity={activity}
          onClose={onClose}
          onToggleFavorite={handleToggleFavorite}
        />

        <DialogContent sx={{ p: 0 }}>
          {/* Calendar content - meeting card or error */}
          {shouldShowCalendarContent && (
            <Box sx={{ p: 3, pb: 0 }}>
              {isCalendarEventNotFound ? (
                <CalendarEventError
                  activityId={activity.Id}
                  activityName={activity.Name}
                />
              ) : (
                <MeetingCard
                  calendarEvent={calendarEvent}
                  isLoading={isCalendarLoading}
                  error={calendarError}
                />
              )}
            </Box>
          )}

          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Left Column - Main Content */}
              <Grid item xs={12} md={8}>
                <CustomerInfoCard
                  activity={activity}
                  onCustomerClick={handleCustomerClick}
                  onUpdateDescription={handleUpdateDescription}
                />

                <WorkDetailsCard
                  onAssignClick={() => setShowAssignDialog(true)}
                  activity={activity}
                  onMoveClick={() => setShowMoveDialog(true)}
                  onWorkClick={handleOpenWork}
                />
              </Grid>

              {/* Right Column - Actions */}
              {activity.State !== WorkItemStateEnum.Closed && (
                <Grid item xs={12} md={4}>
                  <ActionPanel onFinishClick={handleFinishClicked} />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>

        <ActivityDialogFooter activity={activity} onClose={onClose} />
      </Dialog>

      {/* Sub-dialogs */}
      <CompleteActionDialog
        open={showCompleteDialog}
        onCancel={() => setShowCompleteDialog(true)}
        onClose={() => setShowCompleteDialog(false)}
        customerNo={activity.Customer.Id}
        onComplete={handleClose}
        workItemNo={activity.Id}
      />

      <MoveWorkItemDialog
        open={showMoveDialog}
        onClose={() => setShowMoveDialog(false)}
        onComplete={() => setShowMoveDialog(false)}
        workItemNo={activity.Id}
      />

      <AssignWorkItemDialog
        open={showAssignDialog}
        onClose={() => setShowAssignDialog(false)}
        workItem={activity}
      />
    </>
  );
};

export default ActivityDialog;
